import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import TaskSubNav from '../components/TaskSubNav';
import TaskAttachments from '../components/TaskAttachments';
import { DSBackLink, DSButton, DSInput, DSSelect, DSCard, DSIcon, DSBadge } from '../design-system/primitives';
import './taskManagementShared.css';

function TaskSuggestionReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const [assignToSuggester, setAssignToSuggester] = useState(false);
  const [pointValue, setPointValue] = useState(1);
  const [bountyEnabled, setBountyEnabled] = useState(false);
  const [bountyAmount, setBountyAmount] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiService.getTaskSuggestions()
      .then((res) => {
        const found = (res.data || []).find((s) => String(s.id) === String(id));
        setSuggestion(found || null);
        setAssignToSuggester(!!found?.wants_to_do_it);
      })
      .catch((err) => setError(err.response?.data?.error || 'Unable to load suggestion'))
      .finally(() => setLoading(false));
    apiService.getTaskCategories().then((res) => setCategories(res.data || [])).catch(() => {});
  }, [id]);

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      await apiService.approveTaskSuggestion(id, {
        assignToSuggester,
        pointValue,
        bountyAmount: bountyEnabled ? Number(bountyAmount) : null,
        categoryId: categoryId || null
      });
      navigate('/tasks/needs-attention', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setSubmitting(true);
      await apiService.rejectTaskSuggestion(id);
      navigate('/tasks/needs-attention', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading...</div>;
  }

  if (!suggestion) {
    return <div className="error-message">Suggestion not found or already reviewed</div>;
  }

  return (
    <div className="tm-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>
      <div className="tm-section">
        <h1>Review Suggestion</h1>
        {error && <div className="error-message">{error}</div>}

        <DSCard variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg" className="tm-suggestion-card">
          <div className="tm-suggestion-header">
            <strong>{suggestion.suggested_by_name}</strong>
            <span className="tm-suggestion-meta">{new Date(suggestion.created_at).toLocaleDateString()}</span>
          </div>
          {suggestion.wants_to_do_it && <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="user" />}>Wants to do it</DSBadge>}
          <h3>{suggestion.title}</h3>
          <p className="tm-preserve-lines">{suggestion.description}</p>
          <TaskAttachments owner="suggestion" ownerId={suggestion.id} />
        </DSCard>

        <div className="tm-form-row">
          <div className="tm-form-group">
            <DSInput label="Points" type="number" min={1} value={pointValue} onChange={(e) => setPointValue(Number(e.target.value))} />
          </div>
          <div className="tm-form-group">
            <label>
              <input type="checkbox" checked={bountyEnabled} onChange={(e) => setBountyEnabled(e.target.checked)} />
              {' '}Bounty (credits)
            </label>
            {bountyEnabled && (
              <DSInput type="number" min={0} step="0.01" value={bountyAmount} onChange={(e) => setBountyAmount(e.target.value)} />
            )}
          </div>
        </div>

        <div className="tm-form-group">
          <DSSelect label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">None</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </DSSelect>
          {categoryId && categories.find((c) => String(c.id) === String(categoryId))?.default_assignee_name && (
            <div className="tm-suggestion-meta">
              This category assigns to {categories.find((c) => String(c.id) === String(categoryId)).default_assignee_name} by default.
            </div>
          )}
        </div>

        {suggestion.wants_to_do_it && (
          <div className="tm-form-group">
            <label>
              <input type="checkbox" checked={assignToSuggester} onChange={(e) => setAssignToSuggester(e.target.checked)} />
              {' '}Assign this task to {suggestion.suggested_by_name} on approval
            </label>
          </div>
        )}

        <div className="tm-actions">
          <DSButton appearance="solid" tone="primary" onClick={handleApprove} disabled={submitting} leadingIcon={<DSIcon name="checkCircle" />}>Approve & Convert to Task
          </DSButton>
          <DSButton appearance="outline" tone="secondary" onClick={handleReject} disabled={submitting} leadingIcon={<DSIcon name="xCircle" />}>Reject Suggestion
          </DSButton>
        </div>
        <p className="tm-audit-note">Rejection doesn't require a reason, but the member will see their suggestion was reviewed.</p>
      </div>
      <TaskSubNav />
    </div>
  );
}

export default TaskSuggestionReview;
